import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Btn } from "./ui";

const VERSAO_LOCAL = process.env.REACT_APP_VERSION || 'dev-local';
const ADIAR_MS = 10 * 60 * 1000; // "Depois" some por 10 min e volta

// Polling de /version.json a cada 5min - quando a versão do servidor
// difere do bundle embutido, mostra o aviso pedindo reload. Clicar
// limpa SW + caches + localStorage transientes e força hard reload.
//
// Visual (plano de layout, 24/09): cartão branco embaixo, na posição dos
// toasts, com "Depois" — antes era uma faixa laranja sólida colada no topo,
// sem como dispensar, por cima até da tela de login.

// Dentro do app nativo (Capacitor) o build é embutido: "Atualizar" recarrega
// o MESMO bundle e o aviso volta na hora — quem atualiza o app é a loja.
const ehNativo = () => { try { return window.Capacitor?.isNativePlatform?.() === true; } catch { return false; } };

// oculto: telas de mapa/ação (rota do freteiro e do locador, oferta de frete,
// rastreamento) — o cartão ficava por cima do "ARRASTE PARA INICIAR" e o
// freteiro não conseguia começar a corrida (25/09). semNav: tela sem a barra
// inferior → o cartão desce até o rodapé em vez de flutuar 5,5 rem acima.
export const BannerNovaVersao = ({ oculto = false, semNav = false }) => {
  const [novaVersao, setNovaVersao] = useState(null);
  const [atualizando, setAtualizando] = useState(false);
  const [adiadoAte, setAdiadoAte] = useState(0);

  useEffect(() => {
    let cancelado = false;

    const checar = async () => {
      try {
        // Cache-busting: garante leitura fresca mesmo com cache do navegador
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelado) return;
        if (d?.version && d.version !== VERSAO_LOCAL && VERSAO_LOCAL !== 'dev-local') {
          setNovaVersao(d.version);
        }
      } catch (e) { /* offline ou erro — silencioso */ }
    };

    checar(); // 1ª checagem imediata
    const interval = setInterval(checar, 5 * 60 * 1000); // a cada 5 min
    // Também checa quando a aba volta a ficar visível (user retorna depois de tempo)
    const onVisible = () => { if (document.visibilityState === 'visible') checar(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelado = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Quando adiado, volta a aparecer sozinho ao fim do prazo.
  useEffect(() => {
    if (!adiadoAte) return undefined;
    const t = setTimeout(() => setAdiadoAte(0), Math.max(0, adiadoAte - Date.now()));
    return () => clearTimeout(t);
  }, [adiadoAte]);

  const atualizarAgora = async () => {
    setAtualizando(true);
    try {
      // Limpa SW + caches + localStorage transiente
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
        }
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch (e) { /* segue pra reload de qualquer jeito */ }
    // Reload com cache-busting da própria URL
    window.location.replace(window.location.pathname + '?v=' + Date.now());
  };

  if (!novaVersao || oculto || ehNativo() || (adiadoAte && Date.now() < adiadoAte)) return null;

  // z-900: acima do mapa e da rota (700), abaixo do Modal (1000)
  return (
    <div
      role="status"
      data-aviso-flutuante="nova-versao"
      className={`fixed z-[900] left-3 right-3 ${semNav ? 'bottom-[max(1rem,env(safe-area-inset-bottom))]' : 'bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5rem))]'}
                 sm:left-auto sm:right-4 sm:bottom-4 sm:w-full sm:max-w-sm
                 bg-white rounded-2xl shadow-[0_8px_30px_rgba(2,6,23,0.18)] ring-1 ring-black/5 overflow-hidden
                 animate-[toastIn_.22s_ease-out]`}
    >
      <div className="flex items-start gap-3">
        <span className="w-1.5 self-stretch flex-shrink-0 bg-orange-500" />
        <span className="mt-3 w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
          <RefreshCw size={16} className="text-orange-500" />
        </span>
        <div className="py-3 pr-4 min-w-0 flex-1">
          <p className="text-[13px] font-bold text-[#12192a] leading-snug">Nova versão disponível</p>
          <p className="text-[13px] text-slate-600 leading-snug">Atualize para receber as últimas correções.</p>
          <div className="flex gap-2 mt-2.5">
            <Btn v="white" sz="sm" onClick={() => setAdiadoAte(Date.now() + ADIAR_MS)} disabled={atualizando}>Depois</Btn>
            <Btn v="primary" sz="sm" loading={atualizando} onClick={atualizarAgora}>
              {atualizando ? 'Atualizando…' : 'Atualizar'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};
